const CALENDAR_API_BASE = "https://www.googleapis.com/calendar/v3";

const authHeaders = (token) => ({
  Authorization: `Bearer ${token}`,
});

const doCalendarFetch = async (path, token) => {
  const res = await fetch(`${CALENDAR_API_BASE}${path}`, {
    headers: authHeaders(token),
  });
  if (!res.ok) {
    const text = await res.text();
    let message = text || `Calendar request failed: ${res.status}`;
    try {
      const parsed = JSON.parse(text || "{}");
      const activationUrl =
        parsed?.error?.details
          ?.find((item) => item?.["@type"]?.includes("ErrorInfo"))
          ?.metadata?.activationUrl || null;
      const reason = parsed?.error?.details
        ?.find((item) => item?.["@type"]?.includes("ErrorInfo"))
        ?.reason;

      if (reason === "SERVICE_DISABLED" || parsed?.error?.status === "PERMISSION_DENIED") {
        message =
          "Google Calendar API is disabled for this Firebase/Google project. Enable it in Google Cloud Console, wait a minute, then retry." +
          (activationUrl ? `\n${activationUrl}` : "");
      } else if (parsed?.error?.message) {
        message = parsed.error.message;
      }
    } catch (error) {
      // keep raw message
    }
    throw new Error(message);
  }
  return res.json();
};

const toIsoString = (value) => {
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const normalizeEvent = (calendarId, calendarName, event) => {
  const startRaw = event?.start?.dateTime || event?.start?.date || null;
  const endRaw = event?.end?.dateTime || event?.end?.date || null;
  const start = toIsoString(startRaw);
  const end = toIsoString(endRaw);
  if (!start || !end) return null;
  return {
    id: String(event?.id || ""),
    calendarId,
    calendarName,
    summary: String(event?.summary || "Busy"),
    start,
    end,
    location: String(event?.location || "").trim() || null,
    allDay: Boolean(event?.start?.date),
  };
};

export const fetchCalendarEvents = async (
  accessToken,
  { daysAhead = 14, perCalendarLimit = 40 } = {}
) => {
  if (!accessToken) {
    throw new Error("Missing Google Calendar access token.");
  }

  const now = new Date();
  const end = new Date(now.getTime() + Math.max(1, daysAhead) * 24 * 60 * 60 * 1000);

  const calendarsPayload = await doCalendarFetch("/users/me/calendarList", accessToken);
  const calendars = Array.isArray(calendarsPayload?.items)
    ? calendarsPayload.items.filter((calendar) => calendar?.selected !== false)
    : [];

  const eventResponses = await Promise.all(
    calendars.slice(0, 15).map(async (calendar) => {
      const params = new URLSearchParams({
        singleEvents: "true",
        orderBy: "startTime",
        maxResults: String(perCalendarLimit),
        timeMin: now.toISOString(),
        timeMax: end.toISOString(),
      });
      const payload = await doCalendarFetch(
        `/calendars/${encodeURIComponent(calendar.id)}/events?${params.toString()}`,
        accessToken
      );
      return {
        calendarId: calendar.id,
        calendarName: calendar.summary || calendar.id,
        items: Array.isArray(payload?.items) ? payload.items : [],
      };
    })
  );

  const merged = eventResponses
    .flatMap((entry) =>
      entry.items.map((event) => normalizeEvent(entry.calendarId, entry.calendarName, event))
    )
    .filter(Boolean)
    .sort((a, b) => new Date(a.start).getTime() - new Date(b.start).getTime())
    .slice(0, 300);

  return merged;
};
